import React from "react";
import { Habit, Day, ReportPeriod } from "../types";
import { getDayScore } from "../utils/habitUtils";
import { getDayColor } from "../getDayColor";
import { generatePeriodDays, getPeriodLabel } from "../utils/periodUtils";

interface ScoreHeatmapProps {
  habits: Habit[];
  days: Day[];
  period: ReportPeriod;
  selectedDay: string;
  onDayClick: (day: string) => void;
}

export const ScoreHeatmap: React.FC<ScoreHeatmapProps> = ({
  habits,
  days,
  period,
  selectedDay,
  onDayClick,
}) => {
  const dateArray = generatePeriodDays(period);
  
  const isToday = (date: string) => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return date === `${year}-${month}-${day}`;
  };

  const isFutureDay = (date: string) => {
    return new Date(date) > new Date();
  };

  // Calculate optimal number of columns for the container
  const maxCols = Math.min(dateArray.length, 14); // Max 14 columns to prevent horizontal scroll
  const rows = Math.ceil(dateArray.length / maxCols);

  return (
    <div style={{ marginBottom: "20px" }}>
      <h3 style={{ marginBottom: "12px", fontSize: "18px" }}>
        Activity Heatmap - Last {getPeriodLabel(period)}
      </h3>
      
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "3px",
        maxWidth: "100%",
        justifyContent: "flex-start",
      }}>
        {dateArray.map((date) => {
          const dayScore = getDayScore(date, days);
          const habitCount = days.find(d => d.date === date)?.habits.length || 0;
          const backgroundColor = getDayColor(habits, dayScore, habitCount);
          const isSelected = date === selectedDay;
          const isCurrentDay = isToday(date);
          const isFuture = isFutureDay(date);

          return (
            <div
              key={date}
              onClick={() => !isFuture && onDayClick(date)}
              style={{
                width: "20px",
                height: "20px",
                backgroundColor: isFuture ? "#f5f5f5" : backgroundColor,
                border: isSelected ? "2px solid rgba(128,128,128,0.8)" : 
                        isCurrentDay ? "1px solid #007bff" : "1px solid #e1e5e9",
                borderRadius: "3px",
                cursor: isFuture ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                color: isFuture ? "#ccc" : "#000",
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                flexShrink: 0,
              }}
              title={`${new Date(date).toLocaleDateString()} - Score: ${dayScore}`}
            >
              {period === "week" || period === "10days" ? new Date(date).getDate() : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
};